import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import ReactDOM from "react-dom";

import Menu from "@mui/material/Menu";
import { makeStyles } from "@mui/styles";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import LocationOff from "@mui/icons-material/LocationOff";

import { Edit, AddNew, Delete, AddToDeviceGroup, Enlarge, ChevronRight } from "orion-components/CBComponents/Icons";
import NestedMenu from "./NestedMenu";
import AddToDeviceGroupDialog from "../../DeviceView/components/AddToDeviceGroupDialog";
import { AddToCompositeDevice, AssignDeviceType } from "../../../CBComponents/Icons";
import { getTranslation } from "orion-components/i18n/Actions/index.js";
import { getDir } from "orion-components/i18n/Config/selector";

const styles = {
	container: { width: "100%", height: "100%" },
	menuWrapper: (contextMenu, dir) => ({
		position: "absolute",
		top: contextMenu?.mouseY,
		...(dir === "ltr" && { left: contextMenu?.mouseX }),
		...(dir === "rtl" && { left: contextMenu?.mouseX - 150 }),
		zIndex: 100000,
		background: "#28292B no-repeat padding-box",
		color: "#fff",
		borderRadius: 4,
		opacity: 1
	}),
	menuItems: {
		width: 210,
		minHeight: 40,
		maxHeight: 210,
		overflowY: "auto"
	},
	divider: {
		height: 1,
		backgroundColor: "#444",
		margin: "2px 0px"
	},
	options: (hoveredItem, dir, value) => ({
		paddingTop: 6.5,
		paddingBottom: 6.5,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between", // Ensure items are spaced between
		cursor: value?.disabled ? "default" : "pointer",
		...(dir === "ltr" && { textAlign: "left" }),
		...(dir === "rtl" && { textAlign: "right" }),
		fontSize: "10px",
		lineHeight: "24px",
		fontFamily: "Roboto",
		fontWeight: "normal",
		fontStyle: "normal",
		letterSpacing: "0px",
		color: "#FFFFFF",
		opacity: value?.disabled ? 0.5 : 1,
		backgroundColor: hoveredItem === value.id ? "#484A4E" : "#28292B"
	}),
	menuIcon: dir => ({
		display: "flex",
		...(dir === "ltr" && { paddingLeft: 17 }),
		...(dir === "rtl" && { paddingRight: 17 })
	}),
	chevron: dir => ({
		...(dir === "ltr" && { paddingRight: 10, marginLeft: "auto" }),
		...(dir === "rtl" && { paddingLeft: 10, marginRight: "auto" })
	}),
	item: (value, dir) => ({
		...(dir === "ltr" && { paddingLeft: value?.icon ? 13.5 : 17.85 }),
		...(dir === "rtl" && { paddingRight: value?.icon ? 13.5 : 17.85 })
	})
};

const useMenuStyles = makeStyles({
	paper: {
		overflow: "unset"
	},
	menu: {
		padding: 0
	}
});

const RightClickMenu = ({
	menuFor,
	contextMenu,
	onClose,
	getSelectedMenuItem,
	compositeDeviceRolledUpCommands = [],
	selectedRows,
	basicDeviceCommands = [],
	hiddenMenuItems = [],
	node,
	readOnly,
	deviceGroupRolledUpCommands,
	allDevices
}) => {
	const menuClasses = useMenuStyles();

	const hasCompositeCommands = compositeDeviceRolledUpCommands?.length > 0;
	const hasDeviceGroupCommands = deviceGroupRolledUpCommands?.length > 0;
	const hasBasicCommands = basicDeviceCommands?.length > 0;
	const dir = useSelector(state => getDir(state));

	// only allow deletion of composite device if it has no devices assigned to it.
	const canDeleteCompositeDevice =
		allDevices?.length &&
		menuFor === "compositeDevice" &&
		!allDevices?.find(device => device?.compositeDeviceId === node?.id);

	const [hoveredItem, setHoveredItem] = useState(null);
	const [nestedMenuAnchorEl, setNestedMenuAnchorEl] = useState(null);

	let menuItems = [];
	let nestedMenuItemsByParentId = {};
	switch (menuFor) {
		case "group":
			menuItems = [
				...(!readOnly
					? [
							{
								id: 1,
								canAccessMenu: true,
								icon: <Edit />,
								item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.edit"),
								action: () => {
									getSelectedMenuItem("Edit");
								},
								hasNestedMenu: false
							},
							{
								id: 2,
								canAccessMenu: true,
								icon: <AddNew />,
								item: getTranslation(
									"global.deviceManager.deviceTree.components.rightClickMenu.addSubGroup"
								),
								action: () => {
									getSelectedMenuItem("Add-sub-group");
								},
								hasNestedMenu: false
							},
							{
								id: 3,
								canAccessMenu: true,
								icon: <Delete />,
								item: getTranslation(
									"global.deviceManager.deviceTree.components.rightClickMenu.deleteGroup"
								),
								action: () => {
									getSelectedMenuItem("Delete");
								},
								hasNestedMenu: false,
								disabled: node?.id === "root"
							}
						]
					: []),
				...(hasDeviceGroupCommands ? deviceGroupRolledUpCommands : [])
			];
			break;

		case "compositeDevice":
			menuItems = [
				{
					id: 1,
					canAccessMenu: true,
					icon: <Enlarge />,
					item: "Open Item",
					action: () => {
						getSelectedMenuItem("openItem");
					},
					hasNestedMenu: false
				},
				...(hasCompositeCommands
					? [{ id: 2, item: "divider" }, ...compositeDeviceRolledUpCommands, { id: 5, item: "divider" }]
					: []),
				...(!readOnly
					? [
							{
								id: 6,
								canAccessMenu: true,
								item: getTranslation(
									"global.deviceManager.deviceTree.components.rightClickMenu.manageDevice"
								),
								action: () => {},
								hasNestedMenu: true
							}
						]
					: [])
			];
			nestedMenuItemsByParentId = {
				6: [
					{
						id: 1,
						canAccessMenu: true,
						icon: <AssignDeviceType />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.assignDeviceType"
						),
						action: () => {
							getSelectedMenuItem("assignDeviceType");
						},
						hasNestedMenu: false
					},
					{
						id: 2,
						canAccessMenu: true,
						icon: <AddToDeviceGroup />,
						item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.addToGroup"),
						action: () => {
							getSelectedMenuItem("addToGroup");
						},
						hasNestedMenu: false
					},
					{
						id: 3,
						canAccessMenu: true,
						icon: <Delete />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.removeFromGroup"
						),
						action: () => {
							getSelectedMenuItem("removeFromGroup");
						},
						hasNestedMenu: false
					},
					...(canDeleteCompositeDevice
						? [
								{
									id: 4,
									canAccessMenu: true,
									icon: <Delete />,
									item: getTranslation(
										"global.deviceManager.deviceTree.components.rightClickMenu.deleteCompositeDevice"
									),
									action: () => {
										getSelectedMenuItem("deleteCompositeDevice");
									},
									hasNestedMenu: false
								}
							]
						: []),
					...(node?.entityData?.geometry
						? [
								{
									id: 6,
									canAccessMenu: true,
									icon: <LocationOff />,
									item: getTranslation(
										"global.deviceManager.deviceTree.components.rightClickMenu.unmap"
									),
									action: () => {
										getSelectedMenuItem("unmap");
									},
									hasNestedMenu: false
								}
							]
						: [])
				]
			};
			break;

		case "compositeDevice_actionIconClick":
			menuItems = [
				{
					id: 1,
					canAccessMenu: true,
					icon: <AssignDeviceType />,
					item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.assignDeviceType"),
					action: () => {
						getSelectedMenuItem("assignDeviceType");
					},
					hasNestedMenu: false
				},
				{
					id: 2,
					canAccessMenu: true,
					icon: <AddToDeviceGroup />,
					item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.addToGroup"),
					action: () => {
						getSelectedMenuItem("addToGroup");
					},
					hasNestedMenu: false
				},
				{
					id: 3,
					canAccessMenu: true,
					icon: <Delete />,
					item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.removeFromGroup"),
					action: () => {
						getSelectedMenuItem("removeFromGroup");
					},
					hasNestedMenu: false
				}
			];
			break;

		case "basicDevice":
			const isActualDevice = node?.entityType === "device";
			menuItems = [
				{
					id: 1,
					canAccessMenu: true,
					icon: <Enlarge />,
					item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.openItem"),
					action: () => {
						getSelectedMenuItem("openItem");
					},
					hasNestedMenu: false
				},
				...(!readOnly
					? [
							{
								id: 6,
								canAccessMenu: true,
								item: getTranslation(
									"global.deviceManager.deviceTree.components.rightClickMenu.manageDevice"
								),
								action: () => {},
								hasNestedMenu: true
							},
							...(isActualDevice && hasBasicCommands
								? [
										{
											id: 7,
											canAccessMenu: true,
											item: getTranslation(
												"global.deviceManager.deviceTree.components.rightClickMenu.controls"
											),
											action: () => {},
											hasNestedMenu: true
										}
									]
								: [])
						]
					: [])
			];

			nestedMenuItemsByParentId = {
				6: [
					{
						id: 1,
						canAccessMenu: true,
						icon: <AssignDeviceType />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.assignDeviceType"
						),
						action: () => {
							getSelectedMenuItem("assignDeviceType");
						},
						hasNestedMenu: false
					},
					{
						id: 2,
						canAccessMenu: true,
						icon: <AddToCompositeDevice />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.addToCompositeDevice"
						),
						action: () => {
							getSelectedMenuItem("addToCompositeDevice");
						},
						hasNestedMenu: false
					},
					{
						id: 3,
						canAccessMenu: true,
						icon: <Delete />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.removeFromCompositeDevice"
						),
						action: () => {
							getSelectedMenuItem("removeFromCompositeDevice");
						},
						hasNestedMenu: false
					},
					{
						id: 4,
						canAccessMenu: true,
						icon: <AddToDeviceGroup />,
						item: getTranslation("global.deviceManager.deviceTree.components.rightClickMenu.addToGroup"),
						action: () => {
							getSelectedMenuItem("addToGroup");
						},
						hasNestedMenu: false
					},
					{
						id: 5,
						canAccessMenu: true,
						icon: <Delete />,
						item: getTranslation(
							"global.deviceManager.deviceTree.components.rightClickMenu.removeFromGroup"
						),
						action: () => {
							getSelectedMenuItem("removeFromGroup");
						},
						hasNestedMenu: false
					},
					...(node?.entityData?.geometry
						? [
								{
									id: 6,
									canAccessMenu: true,
									icon: <LocationOff />,
									item: getTranslation(
										"global.deviceManager.deviceTree.components.rightClickMenu.unmap"
									),
									action: () => {
										getSelectedMenuItem("unmap");
									},
									hasNestedMenu: false
								}
							]
						: [])
				],
				...(isActualDevice && hasBasicCommands ? { 7: basicDeviceCommands } : {})
			};
			break;
		default:
			menuItems = [];
	}

	// Filter out hidden menu items
	const filterHiddenMenuItems = items =>
		items.filter(
			item =>
				!hiddenMenuItems.some(
					hiddenItem => hiddenItem.menuFor === menuFor && hiddenItem.label === (item.item || item.label)
				)
		);

	menuItems = filterHiddenMenuItems(menuItems);
	nestedMenuItemsByParentId = Object.fromEntries(
		Object.entries(nestedMenuItemsByParentId).map(([menuId, items]) => [menuId, filterHiddenMenuItems(items)])
	);

	return ReactDOM.createPortal(
		<div style={styles.container}>
			{contextMenu && menuItems?.length ? (
				<div style={styles.menuWrapper(contextMenu, dir)} onMouseLeave={onClose} data-testid="orion-components-device-manager-device-tree-components-right-click-menu-div">
					<div style={styles.menuItems}>
						{menuItems.map((value, index) => {
							if (value.item === "divider") {
								return <div key={index} style={styles.divider}></div>;
							}

							return (
								<div
									key={value.id}
									onClick={() => {
										if (!value.hasNestedMenu && !value?.disabled) {
											value.action();
											onClose();
										}
									}}
									onMouseEnter={e => {
										e.currentTarget.style.backgroundColor = "#484A4E";
										if (value.hasNestedMenu) {
											setHoveredItem(value.id);
											setNestedMenuAnchorEl(e.currentTarget);
										}
									}}
									onMouseLeave={e => {
										e.currentTarget.style.backgroundColor = "#28292B";
										if (value.hasNestedMenu) {
											setHoveredItem(null);
											setNestedMenuAnchorEl(null);
										}
									}}
									style={styles.options(hoveredItem, dir, value)} data-testid="orion-components-device-manager-device-tree-components-right-click-menu-div-2"
								>
									<div style={{ display: "flex", alignItems: "center" }}>
										{value?.icon && <div style={styles.menuIcon(dir)}>{value.icon}</div>}
										<div style={styles.item(value, dir)}>{value.item}</div>
									</div>

									{value.hasNestedMenu && (
										<div style={styles.chevron(dir)}>
											{dir === "rtl" ? <ChevronLeft /> : <ChevronRight />}
										</div>
									)}
									<Menu
										style={{ pointerEvents: "none" }}
										anchorEl={nestedMenuAnchorEl}
										anchorOrigin={{
											horizontal: dir === "rtl" ? "left" : "right",
											vertical: "top"
										}}
										transformOrigin={{
											horizontal: dir === "rtl" ? "right" : "left",
											vertical: "top"
										}}
										open={hoveredItem === value.id}
										autoFocus={false}
										disableAutoFocus
										disableEnforceFocus
										classes={{ paper: menuClasses.paper, list: menuClasses.menu }} data-testid="orion-components-device-manager-device-tree-components-right-click-menu-menu"
									>
										{hoveredItem === value.id && (
											<NestedMenu nestedMenuContent={nestedMenuItemsByParentId[value.id] || []} />
										)}
									</Menu>
								</div>
							);
						})}
					</div>
				</div>
			) : (
				<></>
			)}
			<AddToDeviceGroupDialog selectedRows={selectedRows} />
		</div>,
		document.body // Moves the element to the body
	);
};

export default RightClickMenu;
